import { useState } from "react"
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose } from "@/components/ui/dialog"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { X } from "lucide-react"
import trialLogo from '@/assets/images/idata-logo.gif'
import { userApi } from '@/http/apiList'
import { rsaEncrypt, pubKey } from "@/utils/encrypt"
import { toast } from 'sonner'


export default function ForgotPasswordModal({ open, onOpenChange, onForgotPasswordClick }) {
  const [step, setStep] = useState("enter-email") // 'enter-email' or 'reset-password'
  const [isLoading, setIsLoading] = useState(false)
  const [email, setEmail] = useState("")
  const [verifyCode, setverifyCode] = useState("")
  const [password, setPassword] = useState("")
  const [confirmPassword, setConfirmPassword] = useState("")
  const [errors, setErrors] = useState({})

  const resetForm = () => {
    setIsLoading(false)
    setEmail("")
    setverifyCode("")
    setPassword("")
    setConfirmPassword("")
    setErrors({})
  }
  
  const handleNextClick = async () => {
    setIsLoading(true)
    setErrors({})
    // Simulate API call to send verification code
    try {
        const data = await userApi.sendCode({
          email: btoa(rsaEncrypt(email, pubKey)),
        });
        if (data.code === 1) {
          toast.success('success');
          setStep("reset-password")
        } else {
          // Handle login failure (e.g., incorrect credentials)
          setErrors({ api: data.subMsg || 'error' })
        }
    } catch (error) {
        // Handle network or other unexpected errors
        setErrors({ api: error?.response?.data?.subMsg || "Failed to connect to the server. Please try again." })
    } finally {
        setIsLoading(false)
    }
  }

  const handleCompleteClick = async () => {
    setIsLoading(true)
    // Simulate API call to reset password
    try {
        const data = await userApi.updatePassword({
          email: btoa(rsaEncrypt(email, pubKey)),
          password: btoa(rsaEncrypt(password, pubKey)),
          confirmPassword: btoa(rsaEncrypt(confirmPassword, pubKey)),
          code: btoa(rsaEncrypt(verifyCode, pubKey)),
        });
        if (data.code === 1) {
        toast.success('success');
        // Reset form
        resetForm();
        onOpenChange(false)
        } else {
        // Handle login failure (e.g., incorrect credentials)
        setErrors({ api: data.subMsg || 'error' })
        }
    } catch (error) {
        // Handle network or other unexpected errors
        setErrors({ api: error?.response?.data?.subMsg || "Failed to connect to the server. Please try again." })
    } finally {
        setIsLoading(false)
    }
    setIsLoading(false)
  }

  const handleOpenChange = (isOpen) => {
    onOpenChange(isOpen)
    // Reset to the first step when modal is closed
    if (!isOpen) {
      setTimeout(() => setStep("enter-email"), 300)
      resetForm()
    }
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[452px] py-[32px] px-[44px] bg-[#fff]">
        {step === "enter-email" && (
          <>
            <DialogHeader className="items-center text-center">
              <img
                src={trialLogo}
                alt="iData Logo"
                className="h-[160px] h-[50px] mb-4"
              />
              <DialogTitle className="text-[30px] text-[#0066FF] font-bold">Forgot Password</DialogTitle>
              <p className="text-[16px] text-[#4d4d4d]">
                Enter your corporate email and we'll send you a code to reset your password.
              </p>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="email-forgot">Corporate Email</Label>
                <Input
                id="email-forgot"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="corporate Email"
                className="placeholder:text-[rgba(77,77,77,0.40)]" />
                {errors.api && <p className="text-sm text-red-500">{errors.api}</p>}
              </div>
            </div>
            <DialogFooter>
              <Button
                onClick={handleNextClick}
                disabled={isLoading}
                className="w-full bg-[#0066ff] hover:bg-[#297FFF] text-[24px] text-white py-6"
              >
                {isLoading ? "Sending..." : "Next"}
              </Button>
            </DialogFooter>
          </>
        )}

        {step === "reset-password" && (
          <>
            <DialogHeader className="items-center text-center">
              <img
                src={trialLogo}
                alt="iData Logo"
                className="h-[160px] h-[50px]"
              />
              <DialogTitle className="text-2xl font-bold text-blue-600">Set New Password</DialogTitle>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid gap-2">
                <Label htmlFor="verification-code">Verification Code</Label>
                <Input id="verification-code" placeholder="Enter Code" className="placeholder:text-[rgba(77,77,77,0.40)]" value={verifyCode}
                onChange={(e) => setverifyCode(e.target.value)} />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="new-password">New Password</Label>
                <Input id="new-password" type="password" placeholder="Enter Password" className="placeholder:text-[rgba(77,77,77,0.40)]" value={password}
                onChange={(e) => setPassword(e.target.value)} />
                <p className="text-sm text-muted-foreground">Combination of letters and special characters(8-20 digit)</p>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="confirm-password">Confirm New Password</Label>
                <Input id="confirm-password" type="password" placeholder="Enter New Password" className="placeholder:text-[rgba(77,77,77,0.40)]" value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)} />
              </div>
              {errors.api && <p className="text-sm text-red-500">{errors.api}</p>}
            </div>
            <DialogFooter>
              <Button
                onClick={handleCompleteClick}
                disabled={isLoading}
                className="w-full bg-[#0066ff] hover:bg-[#297FFF] text-[24px] text-white py-6"
              >
                {isLoading ? "Completing..." : "Complete"}
              </Button>
            </DialogFooter>
          </>
        )}

        <DialogClose className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none data-[state=open]:bg-accent data-[state=open]:text-muted-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </DialogClose>
      </DialogContent>
    </Dialog>
  )
}
